/* eslint-disable no-extend-native */
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import moment from 'moment';
import ptBr from 'moment/locale/pt-br';
import { Modal } from '~/components';
import { Toast } from '~/elements';
import Routes from '~/components/Routes';
import Help from '~/compositions/Help';
import QuickSearch from '~/pages/QuickSearch';
import messages from '~/helpers/api/messages';
import { closeHelp } from '~/store/modules/help/actions';
import {
  loadUserRequest,
  loadUserSuccess,
  updateUser,
} from '~/store/modules/user/actions';
import { closeModal } from '~/store/modules/modal/actions';
import {
  showNewNotifications,
  getNewNotifications,
} from '~/store/modules/newNotifications/actions';
import { onReceiveMessaging } from '~/push-notification';
import CookieConsent from './components/CookieConsent';
import ToastConfig from './config/ToastConfig';
import { ToastCtx } from './providers/ToastProvider';
import { Loader, Helmet } from './components';
import { users } from './helpers/api';
import { svaListRequest } from './store/modules/svaList/actions';
import Notifier from './Notifier';
import './config/firebase';
import axios from '~/config/axios';
import useDynatraceScript from './hooks/useDynatraceScript';
import { urls, VRPAT } from './config/constants';

moment.locale('pt-br', ptBr);

// eslint-disable-next-line func-names
Number.prototype.toBRL = function() {
  return this.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
};

const Container = styled.div`
  width: 100%;
`;
const SendConfirmation = styled.span`
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
`;

function App() {
  const toast = useContext(ToastCtx);
  const [isLoading, setIsLoading] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const modal = useSelector(state => state.modal);
  const openHelp = useSelector(state => state.help.open);
  const user = useSelector(state => state.user);
  const issuer = useSelector(state => state?.issuer);
  const location = useLocation();
  const urlsBlackListed = [
    `/${urls.login}`,
    `/${urls.register}`,
    `/${urls.forgotMyPassword}`,
    `/${urls.createNewPassword}`,
    `/${urls.forgotMyEmail}`,
  ];

  useDynatraceScript();

  useEffect(() => {
    const issuerName = issuer?.name;
    // SVAs only for VR
    if (user?.cpf && issuerName === VRPAT) {
      dispatch(svaListRequest(user?.cpf));
    }
  }, [user?.cpf]);

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }
  const query = useQuery();

  async function handleExpiredLink(email) {
    try {
      setIsLoading(true);
      const response = await users.sendEmailUnlockUser(email);
      toast.dispatch(
        messages(
          response,
          'unlock_user',
          <>
            Enviamos um link para o e-mail {email} para redefinir sua senha e
            desbloquear seu login. Caso não tenha recebido, verifique a sua
            caixa de <strong>spam</strong> ou a <strong>lixeira</strong>.
          </>,
        ),
      );
    } catch (e) {
      toast.dispatch(messages(e, 'unlock_user'));
    } finally {
      setIsLoading(false);
    }
  }

  function msgExpiredLink(email) {
    return (
      <>
        Para receber um novo e-mail de desbloqueio, &nbsp;
        <SendConfirmation onClick={() => handleExpiredLink(email)}>
          clique aqui
        </SendConfirmation>
      </>
    );
  }

  async function handleAcceptConsent() {
    try {
      await users.cookieConsent();
      dispatch(updateUser({ cookieConsent: true }));

      setShowCookieConsent(false);
    } catch (e) {
      toast.dispatch(messages(e, 'cookie_consent'));
    }
  }

  useEffect(() => {
    const validateToken = query.get('validate');
    const resetPasswordToken = query.get('resetpassword');
    const unlockLoginToken = query.get('unlocklogin');

    const exec = async () => {
      if (issuer.name !== undefined) {
        if (validateToken) {
          const isVR = (await issuer.name) === VRPAT;
          const msgKey = isVR
            ? 'register_activation'
            : 'register_activation_partner';

          try {
            setIsLoading(true);
            const response = await users.validateToken(validateToken);

            toast.dispatch(messages(response, msgKey));
          } catch (e) {
            toast.dispatch(messages(e, msgKey));
          } finally {
            setIsLoading(false);
          }
        } else if (resetPasswordToken) {
          sessionStorage.setItem('pass-token', resetPasswordToken);

          history.push({
            pathname: `/${urls.createNewPassword}`,
          });
        } else if (unlockLoginToken) {
          try {
            setIsLoading(true);
            const response = await users.checkStatusToken(unlockLoginToken);
            const status = response?.result?.status;
            if (status === 'PENDENTE') {
              sessionStorage.setItem('pass-token', unlockLoginToken);
              history.push({
                pathname: `/${urls.createNewPassword}`,
              });
            }

            if (status === 'EXPIRADO') {
              toast.dispatch(
                'Link expirado',
                'error',
                msgExpiredLink(response?.result?.email),
              );
              history.push(`/${urls.login}`);
            }
          } catch (e) {
            toast.dispatch(messages(e, 'unlock_user'));
          } finally {
            setIsLoading(false);
          }
        }
      }
    };
    exec();
  }, [issuer]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');

    if (!user.email && token) {
      axios.defaults.headers.common.Authorization = token;

      dispatch(
        loadUserRequest(
          data => {
            const userData = data;

            dispatch(loadUserSuccess(userData));

            onReceiveMessaging(({ title, body }) => {
              dispatch(getNewNotifications());
              dispatch(showNewNotifications(title, body));
            });
          },
          error => {
            toast.dispatch(messages(error, 'find_profile'));
          },
          () => null,
        ),
      );
    }
  }, []);

  useEffect(() => {
    if ('cookieConsent' in user) {
      const url = location.pathname;
      const consent = user?.cookieConsent;

      if (!consent && !urlsBlackListed.includes(url)) {
        setShowCookieConsent(true);
      } else if (consent || urlsBlackListed.includes(url)) {
        setShowCookieConsent(false);
      } else {
        setShowCookieConsent(!consent);
      }
    }
  }, [location.pathname, user.cookieConsent]);

  return (
    <SnackbarProvider
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      content={(key, message) => (
        <Toast key={key}>
          <b>{message.title}</b>
          <br />
          {message.body}
        </Toast>
      )}
    >
      <Loader isLoading={isLoading}>
        <Container>
          <Helmet title={issuer?.head?.title} favicon={issuer?.head?.favicon} />
          <ToastConfig />
          <Routes />
        </Container>
      </Loader>
      <Modal
        open={modal === 'quick_search'}
        maxWidth="lg"
        handleClose={() => dispatch(closeModal())}
      >
        <QuickSearch modalName={modal} />
      </Modal>
      <Help open={openHelp} closeHelp={() => dispatch(closeHelp())} />
      <CookieConsent
        showConsent={showCookieConsent}
        onAccept={handleAcceptConsent}
        onClose={() => setShowCookieConsent(false)}
      />
      <Notifier />
    </SnackbarProvider>
  );
}

export default App;
